/*!
 * ThermoAspen Sauna Designer — Windows/Door Editor (flat wall) v1.1.0
 * Stage 6 of the mobile rebuild (26 Oct 2026). 1.1.0 (29 Oct 2026): the door is edited here too.
 *
 * One wall at a time, drawn flat as seen from inside the room:
 *   ‹ wall ›, tap the window or the door to select it, drag to move, drag the edge handles to
 *   resize, Size (exact width/height), Add, Align, Copy, Delete, Door here, ✕ (cancel) and
 *   Done (save).
 *
 * Windows (rules as Window Editor 1.4.21, thermoaspen-wall-opening-editor-v1.js):
 *   - one window per wall; minimum 400 × 300 mm; 50 mm size steps; inside the finished wall
 *     and ceiling; no overlap with the door, another opening, or same-wall benches/backrests.
 * Door (1.1.0):
 *   - width 700 / 800 / 900 mm and height 1900 / 2000 / 2100 mm (100 mm steps);
 *   - slides along its wall (no vertical move); "Door here" moves it to the wall on screen
 *     (front, left or right wall only — never the back wall);
 *   - may not overlap a window; benches and the stove adapt to it, and the Designer refuses
 *     a door position that leaves no compliant layout.
 * Both: king/jack stud end stops and head limits come from the Opening Constraint Authority,
 * and nothing is saved unless the authority (and, for the door, the Designer) accepts it.
 *
 * Data: ThermoAspenOpeningCollectionV2 (read/write), ThermoAspenOpeningConstraintAuthorityV1
 * (evaluate), __ThermoAspenDesignerV5 (project state, recalculate).
 *
 * Public API: window.ThermoAspenWindowsEditorV1 { version, open({wall, select}), close(), isOpen() }
 *   select: 'door' opens with the door selected (on the door's wall unless a wall is given).
 */
(function (W, D) {
  'use strict';
  if (W.ThermoAspenWindowsEditorV1) return;

  var VERSION = '1.1.0', NEW_W = 600, NEW_H = 600, NEW_SILL = 1300, MIN_W = 400, MIN_H = 300;
  var DOOR_WIDTHS = [700, 800, 900], DOOR_HEIGHTS = [1900, 2000, 2100], DOOR_STEP = 100;
  var DOOR_WALLS = ['front', 'left', 'right'];
  var WALLS = ['back', 'right', 'front', 'left'];
  var NAMES = { back: 'Back wall', right: 'Right wall', front: 'Front wall', left: 'Left wall' };
  var PAD = 140; // mm of space round the wall drawing, for handles

  /* ---------- helpers ---------- */
  function n(v, f) { v = Number(v); return isFinite(v) ? v : (f == null ? 0 : f); }
  function round(v) { return Math.round(n(v)); }
  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
  function clone(v) { try { return JSON.parse(JSON.stringify(v)); } catch (e) { return v; } }
  function $(id) { return D.getElementById(id); }
  function el(tag, cls, text) { var e = D.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
  var SVGNS = 'http://www.w3.org/2000/svg';
  function se(tag, attrs, parent) { var e = D.createElementNS(SVGNS, tag); for (var k in attrs) if (attrs[k] != null) e.setAttribute(k, attrs[k]); if (parent) parent.appendChild(e); return e; }
  function isDoor(o) { return !!o && o.type === 'door'; }
  function noun(o) { return isDoor(o) ? 'Door' : 'Window'; }
  function nearest(list, v) { var best = list[0]; list.forEach(function (x) { if (Math.abs(x - v) < Math.abs(best - v)) best = x; }); return best; }

  /* ---------- data access ---------- */
  function collection() { return W.ThermoAspenOpeningCollectionV2; }
  function solver() { return W.ThermoAspenOpeningConstraintAuthorityV1; }
  function designer() { return W.__ThermoAspenDesignerV5; }
  function room() {
    var w = W.ThermoAspenInteriorWorkspaceV1, r = w && w.getRoom ? w.getRoom() : null;
    if (r && n(r.lengthMm) > 0 && n(r.widthMm) > 0 && n(r.heightMm) > 0) return r;
    var d = W.ThermoAspenSaunaDesignerData, dim = d && d.dimensions;
    return dim && n(dim.lengthMm) > 0 ? { lengthMm: n(dim.lengthMm), widthMm: n(dim.widthMm), heightMm: n(dim.heightMm) } : null;
  }
  function snapshot() { var c = collection(), r = room(); return c && r && c.getState ? c.getState(r) : null; }
  function span(w, r) { return w === 'front' || w === 'back' ? n(r && r.lengthMm) : n(r && r.widthMm); }

  /* ---------- rules (windows from Window Editor 1.4.21, unchanged) ---------- */
  function projectFor(openings) { var d = designer(), q = d && d.getProjectState && d.getProjectState(); if (!q || Number(q.schemaVersion) !== 2) return null; q = clone(q); q.openings = clone(openings); q.room = clone(room()); return q; }
  function evaluation(opening, replace) {
    var s = solver(), q = snapshot(); if (!s || !q || !s.evaluate) return { valid: false, errors: ['STRUCTURAL_AUTHORITY_UNAVAILABLE'] };
    var list = clone(q.openings), i;
    if (replace) { i = list.findIndex(function (x) { return x.id === opening.id; }); if (i < 0) return { valid: false, errors: ['OPENING_NOT_FOUND'] }; list[i] = clone(opening); } else list.push(clone(opening));
    var p = projectFor(list); if (!p) return { valid: false, errors: ['PROJECT_NOT_READY'] };
    try { return s.evaluate(p, opening); } catch (err) { return { valid: false, errors: ['STRUCTURAL_EVALUATION_FAILED'], detail: String(err && err.message || err) }; }
  }
  function collides(o, openings) {
    return openings.some(function (x) {
      if (!x || x.id === o.id || x.wall !== o.wall || x.enabled === false) return false;
      var ax = n(o.offsetMm), bx = n(x.offsetMm), ay = isDoor(o) ? 0 : n(o.sillHeightMm), by = isDoor(x) ? 0 : n(x.sillHeightMm);
      return ax < bx + n(x.frameWidthMm) && ax + n(o.frameWidthMm) > bx && ay < by + n(x.frameHeightMm) && ay + n(o.frameHeightMm) > by;
    });
  }
  function doorSizeOk(o) { return DOOR_WIDTHS.indexOf(round(o.frameWidthMm)) >= 0 && DOOR_HEIGHTS.indexOf(round(o.frameHeightMm)) >= 0; }
  function simpleValid(o, openings, r) {
    if (!o || !r) return false;
    if (isDoor(o)) return DOOR_WALLS.indexOf(o.wall) >= 0 && o.offsetMm >= 0 && doorSizeOk(o) && o.offsetMm + o.frameWidthMm <= span(o.wall, r) && o.frameHeightMm <= n(r.heightMm) && !collides(o, openings);
    return o.offsetMm >= 0 && o.sillHeightMm >= 0 && o.frameWidthMm >= MIN_W && o.frameHeightMm >= MIN_H && o.offsetMm + o.frameWidthMm <= span(o.wall, r) && o.sillHeightMm + o.frameHeightMm <= n(r.heightMm) && !collides(o, openings);
  }
  function isBackrestMember(m) { if (!m) return false; return [m.assemblyType, m.type, m.kind, m.role, m.id, m.name].some(function (v) { return typeof v === 'string' && v.toLowerCase().indexOf('backrest') >= 0; }); }
  function benchNoWindowZones(wall) {
    var d = W.ThermoAspenSaunaSceneData, g = d && d.benchGeometry, a = g && g.members, r = room(), groups = Object.create(null), butt = [], tol = 50;
    if (!Array.isArray(a) || !r) return [];
    function addSame(key, u1, u2, y1, y2, type) {
      var lim = span(wall, r); u1 = clamp(n(u1), 0, lim); u2 = clamp(n(u2), 0, lim);
      if (u2 <= u1 || y2 <= y1) return;
      var z = groups[key]; if (!z) z = groups[key] = { u1: Infinity, u2: -Infinity, y1: Infinity, y2: -Infinity, type: type };
      z.u1 = Math.min(z.u1, u1); z.u2 = Math.max(z.u2, u2); z.y1 = Math.min(z.y1, y1); z.y2 = Math.max(z.y2, y2);
    }
    function addButt(m, u1, u2, y2) {
      var lim = span(wall, r); u1 = clamp(n(u1), 0, lim); u2 = clamp(n(u2), 0, lim); y2 = n(y2);
      if (u2 <= u1 || y2 <= 0) return;
      butt.push({ u1: u1, u2: u2, y1: 0, y2: y2, type: 'bench-butt', memberId: String(m && m.id || '') });
    }
    a.forEach(function (m) {
      if (!m || m.render === false) return;
      var backrest = isBackrestMember(m), type = backrest ? 'backrest' : 'bench';
      var key = String(m.segmentId || m.assemblyId || m.id || 'bench');
      if (m.wall === wall && m.localBoxMm) { var b = m.localBoxMm; addSame('same|' + type + '|' + key, b.u1, b.u2, b.y1, b.y2, type); }
      if (m.wall === wall || backrest || !m.world || !m.world.aabbMm) return;
      var q = m.world.aabbMm, u1, u2, touches = false;
      if (wall === 'back' && n(q.z1) <= tol) { touches = true; u1 = q.x1; u2 = q.x2; }
      else if (wall === 'front' && n(r.widthMm) - n(q.z2) <= tol) { touches = true; u1 = q.x1; u2 = q.x2; }
      else if (wall === 'left' && n(q.x1) <= tol) { touches = true; u1 = q.z1; u2 = q.z2; }
      else if (wall === 'right' && n(r.lengthMm) - n(q.x2) <= tol) { touches = true; u1 = q.z1; u2 = q.z2; }
      if (touches) addButt(m, u1, u2, q.y2);
    });
    return Object.keys(groups).map(function (k) { return groups[k]; }).concat(butt)
      .filter(function (z) { return isFinite(z.u1) && z.u2 > z.u1 && isFinite(z.y1) && z.y2 > z.y1; });
  }
  function overlapsBenchZones(o, zones) { if (!o) return false; var x1 = n(o.offsetMm), x2 = x1 + n(o.frameWidthMm), y1 = n(o.sillHeightMm), y2 = y1 + n(o.frameHeightMm); return (zones || []).some(function (z) { return x1 < z.u2 && x2 > z.u1 && y1 < z.y2 && y2 > z.y1; }); }
  // The door is never blocked by benches: the bench layout adapts to the door.
  function behindBenchOrBackrest(o) { return !!(o && o.wall) && !isDoor(o) && overlapsBenchZones(o, benchNoWindowZones(o.wall)); }
  function zonesFor(o) { return isDoor(o) ? [] : benchNoWindowZones(o.wall); }
  function windowsOnWall(w) { var q = snapshot(); return q ? (q.openings || []).filter(function (x) { return x.type === 'window' && x.wall === w && x.enabled !== false; }) : []; }
  function getDoor() { var q = snapshot(); return q && (q.openings || []).find(function (x) { return x.type === 'door' && x.enabled !== false; }) || null; }
  function doorOnWall(w) { var d = getDoor(); return d && d.wall === w ? d : null; }
  function getWindow(id) { var q = snapshot(); return q && (q.openings || []).find(function (x) { return x.type === 'window' && x.id === id; }) || null; }
  function codeFor(o) { if (isDoor(o)) return 'Door'; var suffix = String(o && o.id || '').match(/\d+$/); return suffix ? 'W' + Number(suffix[0]) : 'Window'; }
  function failure(res) {
    var s = String(res && res.errors && res.errors[0] || 'INVALID_POSITION');
    if (s.indexOf('OPENING_OVERLAP_') === 0 || s === 'OPENING_OUTSIDE_WALL_OR_OVERLAP') return 'Opening overlap or wall edge reached';
    if (s === 'WINDOW_KING_JACK_STUD_CLEARANCE' || s === 'WINDOW_FINISH_OVERLAP_STRUCTURAL_SUPPORT') return 'Window needs an unobstructed king stud and jack stud on each side';
    if (s === 'DOOR_KING_JACK_STUD_CLEARANCE') return 'Door needs a king stud and a jack stud on each side';
    if (s === 'DOOR_BENCH_HEATER_LAYOUT_CONFLICT') return 'The benches and stove can’t be laid out with the door there';
    if (s === 'DESIGNER_DOOR_TRANSACTION_UNAVAILABLE') return 'The designer is still loading';
    if (s === 'OPENING_HEAD_EXCEEDS_STRUCTURAL_LIMIT') return 'Structural head limit reached';
    if (s === 'OPENING_HEAD_EXCEEDS_FINISHED_ROOM_LIMIT') return 'Ceiling limit reached';
    if (s === 'HEADER_STANDARD_SPAN_EXCEEDED') return 'Header span limit reached';
    if (s === 'OPENING_OUTSIDE_WALL') return 'Wall edge reached';
    if (s === 'STRUCTURAL_AUTHORITY_UNAVAILABLE' || s === 'PROJECT_NOT_READY') return 'Structural rules not ready';
    if (s.indexOf('immutable fixed stud') >= 0) return 'Fixed structural stud reached';
    if (/Californian corner minimum|king and jack support extends outside/i.test(s)) return 'Too close to the corner: the wall needs room for a king stud and a jack stud';
    return s.replace(/_/g, ' ').toLowerCase().replace(/^./, function (x) { return x.toUpperCase(); });
  }
  function localIssues(o, others, r, zones) {
    if (!o || !r) return [];
    var N = noun(o), door = isDoor(o);
    var out = [], left = n(o.offsetMm), right = left + n(o.frameWidthMm), bottom = door ? 0 : n(o.sillHeightMm), top = bottom + n(o.frameHeightMm), maxU = span(o.wall, r), maxY = n(r.heightMm), finish = 0;
    function addBoundary(edge, amount, msg) { if (amount > 0) out.push({ kind: 'boundary', edge: edge, amount: Math.ceil(amount), message: msg }); }
    if (door) {
      if (DOOR_WALLS.indexOf(o.wall) < 0) out.push({ kind: 'general', message: 'The door can go on the front, left or right wall' });
      if (DOOR_WIDTHS.indexOf(round(o.frameWidthMm)) < 0) out.push({ kind: 'minimum-width', message: 'Door width must be 700, 800 or 900 mm' });
      if (DOOR_HEIGHTS.indexOf(round(o.frameHeightMm)) < 0) out.push({ kind: 'minimum-height', message: 'Door height must be 1900, 2000 or 2100 mm' });
    } else {
      if (n(o.frameWidthMm) < MIN_W) out.push({ kind: 'minimum-width', message: 'Window width must be at least 400 mm' });
      if (n(o.frameHeightMm) < MIN_H) out.push({ kind: 'minimum-height', message: 'Window height must be at least 300 mm' });
    }
    addBoundary('u0', -finish - left, N + ' exceeds the finished wall boundary by ' + Math.ceil(-finish - left) + ' mm');
    addBoundary('u1', right - maxU - finish, N + ' exceeds the finished wall boundary by ' + Math.ceil(right - maxU - finish) + ' mm');
    if (!door) addBoundary('y0', -bottom, 'Sill extends below the floor by ' + Math.ceil(-bottom) + ' mm');
    addBoundary('y1', top - maxY - finish, N + ' head exceeds the finished ceiling boundary by ' + Math.ceil(top - maxY - finish) + ' mm');
    (others || []).forEach(function (x) {
      if (!x || x.id === o.id || x.enabled === false || x.wall !== o.wall) return;
      var xu = n(x.offsetMm), xv = xu + n(x.frameWidthMm), yb = isDoor(x) ? 0 : n(x.sillHeightMm), yt = yb + n(x.frameHeightMm),
        a = Math.max(left, xu), b = Math.min(right, xv), c = Math.max(bottom, yb), d = Math.min(top, yt);
      if (b > a && d > c) out.push({ kind: 'opening', u1: a, u2: b, y1: c, y2: d, message: N + ' overlaps ' + (isDoor(x) ? 'the door' : (codeFor(x) || 'a window')) });
    });
    (zones || []).forEach(function (z) {
      var a = Math.max(left, n(z.u1)), b = Math.min(right, n(z.u2)), c = Math.max(bottom, n(z.y1)), d = Math.min(top, n(z.y2));
      if (b > a && d > c) out.push({ kind: 'bench', u1: a, u2: b, y1: c, y2: d, message: z.type === 'backrest' ? 'Window overlaps a backrest on this wall' : 'Window overlaps a bench' });
    });
    return out;
  }
  function kingJackSideIssues(result, o) {
    var N = noun(o), L = result && result.limits || {}, S = result && result.support || {}, G = result && result.studGrid || {}, R = result && result.roughOpening || {},
      left = n(o.offsetMm), right = left + n(o.frameWidthMm), sw = Number(L.kingStudWidthMm), min = Number(L.minFrameOffsetForStudsMm), max = Number(L.maxFrameRightForStudsMm), eps = .01, out = [];
    var lOver = Number.isFinite(min) ? min - left : 0, rOver = Number.isFinite(max) ? right - max : 0;
    if (Number.isFinite(sw) && sw > 0) {
      if (Number.isFinite(S.kingLeftMm) && Number.isFinite(G.startMm)) lOver = Math.max(lOver, G.startMm - (S.kingLeftMm - sw / 2));
      if (Number.isFinite(S.kingRightMm) && Number.isFinite(G.endMm)) rOver = Math.max(rOver, (S.kingRightMm + sw / 2) - G.endMm);
    }
    if (Number.isFinite(R.u0Mm) && Number.isFinite(S.innerSupportLeftMm)) lOver = Math.max(lOver, S.innerSupportLeftMm - R.u0Mm);
    if (Number.isFinite(R.u1Mm) && Number.isFinite(S.innerSupportRightMm)) rOver = Math.max(rOver, R.u1Mm - S.innerSupportRightMm);
    if (lOver > eps) out.push({ kind: 'stud-side', edge: 'u0', amount: Math.ceil(lOver - eps), message: 'Left ' + N.toLowerCase() + ' edge conflicts with required king/jack stud support' });
    if (rOver > eps) out.push({ kind: 'stud-side', edge: 'u1', amount: Math.ceil(rOver - eps), message: 'Right ' + N.toLowerCase() + ' edge conflicts with required king/jack stud support' });
    if (!out.length) out.push({ kind: 'general', message: N + ' requires a full king stud and jack stud on each side; adjust its position or width' });
    return out;
  }
  function authorityIssues(result, o) {
    if (!result || result.valid !== false) return [];
    var N = noun(o), out = [], errors = Array.isArray(result.errors) ? result.errors : [], limits = result.limits || {};
    errors.forEach(function (err) {
      var code = String(err || ''), limit = null, msg = '';
      if (code === 'WINDOW_KING_JACK_STUD_CLEARANCE' || code === 'WINDOW_FINISH_OVERLAP_STRUCTURAL_SUPPORT' || code === 'DOOR_KING_JACK_STUD_CLEARANCE' || /Californian corner minimum|king and jack support extends outside/i.test(code)) out = out.concat(kingJackSideIssues(result, o));
      else if (code === 'HEADER_STANDARD_SPAN_EXCEEDED') out.push({ kind: 'header-span', message: 'Opening is too wide for the available standard header' });
      else if (code === 'OPENING_HEAD_EXCEEDS_STRUCTURAL_LIMIT' || code === 'OPENING_HEAD_EXCEEDS_FINISHED_ROOM_LIMIT') {
        var sill = isDoor(o) ? 0 : n(o.sillHeightMm);
        if (Number.isFinite(limits.effectiveMaxFrameHeadMm)) limit = limits.effectiveMaxFrameHeadMm;
        else if (Number.isFinite(limits.effectiveMaxFrameHeightAtCurrentSillMm)) limit = sill + limits.effectiveMaxFrameHeightAtCurrentSillMm;
        msg = code === 'OPENING_HEAD_EXCEEDS_STRUCTURAL_LIMIT' ? N + ' head exceeds its structural limit' : N + ' head exceeds the finished-room limit';
        var overrun = Number.isFinite(limit) ? sill + n(o.frameHeightMm) - limit : null;
        var required = overrun !== null && overrun > 0 ? Math.ceil(overrun - 0.00001) : null;
        out.push({ kind: 'head', limit: limit, requiredMm: required, availableDropMm: sill, message: msg });
      } else out.push({ kind: 'general', message: failure(result) });
    });
    if (!out.length) out.push({ kind: 'general', message: failure(result) });
    return out;
  }
  function issuesFor(o, structuralResult) {
    var r = room(), q = snapshot(), others = (q && q.openings || []).filter(function (x) { return x.id !== o.id; }),
      local = localIssues(o, others, r, zonesFor(o));
    if (structuralResult && structuralResult.valid === false) {
      var extra = authorityIssues(structuralResult, o);
      if (local.length) extra = extra.filter(function (x) { return x.kind !== 'general'; });
      return local.concat(extra);
    }
    return local;
  }
  function issueMessage(issues, fallback, o) {
    if (!issues || !issues.length) return fallback || '';
    var a = issues[0], msg = a.message || fallback || '', door = isDoor(o);
    if (a.kind === 'stud-side') msg += door ? ' — move the door away from this end, or choose a narrower door' : ' — move this edge inward or reduce the width';
    else if (a.kind === 'boundary' && a.amount > 0) {
      if (a.edge === 'u0' || a.edge === 'u1') msg += ' — move inward by at least ' + a.amount + ' mm' + (door ? '' : ' or reduce the width');
      if (a.edge === 'y0') msg += ' — raise the sill or reduce the height';
      if (a.edge === 'y1') msg += door ? ' — choose a lower door' : ' — lower the window or reduce the height';
    } else if (a.kind === 'head') {
      if (door) msg += ' — choose a lower door';
      else if (a.requiredMm > 0) {
        var canLower = Number.isFinite(a.availableDropMm) && a.availableDropMm >= a.requiredMm;
        msg += ' — ' + (canLower ? 'lower by at least ' + a.requiredMm + ' mm, or reduce the height' : 'reduce the height by at least ' + a.requiredMm + ' mm, or move it');
      } else msg += ' — lower the window or reduce the height';
    } else if (a.kind === 'header-span') msg += door ? ' — choose a narrower door' : ' — reduce the width';
    return msg;
  }
  function validateCandidate(o, replace) {
    var r = room(), q = snapshot(), others = q ? (q.openings || []).filter(function (x) { return x.id !== o.id; }) : [];
    if (!r || !simpleValid(o, others, r)) return { valid: false, message: issueMessage(issuesFor(o, null), 'Opening overlap or wall edge reached', o), result: null };
    if (behindBenchOrBackrest(o)) return { valid: false, message: issueMessage(issuesFor(o, null), 'Window conflicts with a bench or backrest', o), result: null };
    var res = evaluation(o, replace);
    return res.valid ? { valid: true, message: '', result: res } : { valid: false, message: issueMessage(issuesFor(o, res), failure(res), o), result: res };
  }
  function newId(openings) { var used = Object.create(null); openings.forEach(function (x) { used[x.id] = 1; }); for (var i = 1; i < 1000; i++) { var id = 'window.' + String(i).padStart(2, '0'); if (!used[id]) return id; } return 'window.' + Date.now(); }
  function initialCentreOffset(o, openings, r) {
    var Wmm = span(o.wall, r), wid = n(o.frameWidthMm), intervals = [{ a: 0, b: Wmm }];
    (openings || []).forEach(function (x) {
      if (!x || x.wall !== o.wall || x.enabled === false || x.id === o.id) return;
      var low = isDoor(x) ? 0 : n(x.sillHeightMm), high = low + n(x.frameHeightMm), oLow = isDoor(o) ? 0 : n(o.sillHeightMm);
      if (oLow >= high || oLow + n(o.frameHeightMm) <= low) return;
      var l = n(x.offsetMm), h = l + n(x.frameWidthMm), next = [];
      intervals.forEach(function (z) { if (h <= z.a || l >= z.b) { next.push(z); return; } if (l > z.a) next.push({ a: z.a, b: l }); if (h < z.b) next.push({ a: h, b: z.b }); }); intervals = next;
    });
    var valid = intervals.filter(function (z) { return z.b - z.a >= wid; });
    if (!valid.length) return round((Wmm - wid) / 2);
    valid.sort(function (a, b) { return (b.b - b.a) - (a.b - a.a); });
    return round((valid[0].a + valid[0].b - wid) / 2);
  }
  function sizeDelta50(v) { return Math.round(n(v) / 50) * 50; }
  // Door heights that fit under the ceiling and the wall's structural head limit (the
  // authority's limit for this wall; a 2100 mm room often allows only a 1900 mm door).
  function doorHeightsAllowed(ceiling, limits) {
    var head = limits && Number.isFinite(limits.effectiveMaxFrameHeadMm) ? limits.effectiveMaxFrameHeadMm : Infinity;
    return DOOR_HEIGHTS.filter(function (x) { return x <= ceiling && x <= head + .1; });
  }
  function translateDoorDrag(d, dx, dy) {
    var q = Object.assign({}, d.initial), r = d.room, wallLength = span(q.wall, r), ceiling = n(r && r.heightMm),
      minSide = d.structuralSides ? Math.max(0, d.structuralSides.min) : 0,
      maxSide = d.structuralSides ? Math.min(wallLength, d.structuralSides.max) : wallLength,
      left = n(d.initial.offsetMm), width = n(d.initial.frameWidthMm), right = left + width, height = n(d.initial.frameHeightMm);
    q.sillHeightMm = 0;
    function fitWidth(want, room) { // largest allowed width <= want that fits in room
      var ok = DOOR_WIDTHS.filter(function (x) { return x <= want && x <= room + .1; });
      return ok.length ? ok[ok.length - 1] : DOOR_WIDTHS[0];
    }
    if (d.kind === 'move') {
      q.offsetMm = round(left + dx);
      if (q.offsetMm < minSide) q.offsetMm = Math.ceil(minSide);
      if (q.offsetMm + width > maxSide) q.offsetMm = Math.floor(maxSide - width);
      if (width > maxSide - minSide) q.offsetMm = Math.ceil(minSide);
    } else if (d.kind === 'left') {
      var lw = fitWidth(clamp(Math.round((width - dx) / DOOR_STEP) * DOOR_STEP, DOOR_WIDTHS[0], DOOR_WIDTHS[DOOR_WIDTHS.length - 1]), right - minSide);
      q.frameWidthMm = lw; q.offsetMm = round(right - lw);
    } else if (d.kind === 'right') {
      var rw = fitWidth(clamp(Math.round((width + dx) / DOOR_STEP) * DOOR_STEP, DOOR_WIDTHS[0], DOOR_WIDTHS[DOOR_WIDTHS.length - 1]), maxSide - left);
      q.frameWidthMm = rw;
    } else if (d.kind === 'top') {
      var hs = doorHeightsAllowed(ceiling, d.limits), want = Math.round((height + dy) / DOOR_STEP) * DOOR_STEP;
      q.frameHeightMm = hs.length ? clamp(want, hs[0], hs[hs.length - 1]) : DOOR_HEIGHTS[0];
    }
    q.frameWidthMm = round(q.frameWidthMm); q.frameHeightMm = round(q.frameHeightMm);
    return q;
  }
  function translateDrag(d, p) {
    var dx = round(p.offsetMm - d.start.offsetMm), dy = round(p.heightMm - d.start.heightMm);
    if (isDoor(d.initial)) return Math.abs(dx) + Math.abs(dy) < 1 ? Object.assign({}, d.initial) : translateDoorDrag(d, dx, dy);
    var q = Object.assign({}, d.initial), r = d.room, wallLength = span(q.wall, r), ceiling = n(r && r.heightMm),
      minSide = d.structuralSides ? Math.max(0, d.structuralSides.min) : 0,
      maxSide = d.structuralSides ? Math.min(wallLength, d.structuralSides.max) : wallLength,
      left = n(d.initial.offsetMm), width = n(d.initial.frameWidthMm), right = left + width,
      sill = n(d.initial.sillHeightMm), height = n(d.initial.frameHeightMm), head = sill + height;
    if (Math.abs(dx) + Math.abs(dy) < 1) return q;
    if (d.kind === 'move') {
      q.offsetMm = left + dx; q.sillHeightMm = round(sill + dy);
      if (q.offsetMm < minSide) q.offsetMm = minSide;
      if (q.offsetMm + width > maxSide) q.offsetMm = maxSide - width;
      if (width > maxSide - minSide) q.offsetMm = minSide;
      if (q.sillHeightMm < 0) q.sillHeightMm = 0;
      if (q.sillHeightMm + height > ceiling) q.sillHeightMm = ceiling - height;
      if (height > ceiling) q.sillHeightMm = 0;
    } else if (d.kind === 'left' || d.kind === 'right') {
      if (d.initial.horizontalPositionMode === 'centred') {
        var change = sizeDelta50((d.kind === 'left' ? -2 : 2) * dx), centre = left + width / 2, capped = Math.min(2 * (centre - minSide), 2 * (maxSide - centre));
        q.frameWidthMm = Math.max(MIN_W, Math.min(width + change, capped)); q.offsetMm = centre - q.frameWidthMm / 2;
      } else if (d.kind === 'left') {
        var lw = Math.max(MIN_W, width + sizeDelta50(-dx)), pl = right - lw;
        if (pl < minSide) { pl = minSide; lw = right - pl; }
        q.frameWidthMm = Math.max(MIN_W, lw); q.offsetMm = right - q.frameWidthMm;
      } else {
        var rw = Math.max(MIN_W, width + sizeDelta50(dx));
        if (left + rw > maxSide) rw = maxSide - left;
        q.frameWidthMm = Math.max(MIN_W, rw);
      }
    } else if (d.kind === 'top') {
      q.frameHeightMm = Math.max(MIN_H, height + sizeDelta50(dy));
      if (sill + q.frameHeightMm > ceiling) q.frameHeightMm = Math.max(MIN_H, ceiling - sill);
    } else if (d.kind === 'bottom') {
      q.frameHeightMm = Math.max(MIN_H, height + sizeDelta50(-dy));
      if (head - q.frameHeightMm < 0) q.frameHeightMm = head;
      q.sillHeightMm = round(head - q.frameHeightMm);
    }
    q.sillHeightMm = round(q.sillHeightMm); q.frameWidthMm = round(q.frameWidthMm); q.frameHeightMm = round(q.frameHeightMm);
    return q;
  }
  function alignmentReference(o) {
    if (!o || isDoor(o)) return null;
    var q = snapshot(); if (!q) return null;
    var cx = n(o.offsetMm) + n(o.frameWidthMm) / 2, ownNo = Number((String(o.id || '').match(/\d+$/) || ['0'])[0]);
    var wins = (q.openings || []).filter(function (x) { return x && x.type === 'window' && x.enabled !== false && x.id !== o.id; })
      .sort(function (a, b) {
        var as = a.wall === o.wall, bs = b.wall === o.wall;
        if (as !== bs) return as ? -1 : 1;
        if (as) return Math.abs(n(a.offsetMm) + n(a.frameWidthMm) / 2 - cx) - Math.abs(n(b.offsetMm) + n(b.frameWidthMm) / 2 - cx);
        var an = Number((String(a.id || '').match(/\d+$/) || ['0'])[0]), bn = Number((String(b.id || '').match(/\d+$/) || ['0'])[0]);
        return Math.abs(an - ownNo) - Math.abs(bn - ownNo) || an - bn;
      });
    if (wins.length) { var w = wins[0]; return { id: w.id, code: codeFor(w), kind: 'window', head: n(w.sillHeightMm) + n(w.frameHeightMm), width: n(w.frameWidthMm), height: n(w.frameHeightMm) }; }
    var doors = (q.openings || []).filter(function (x) { return x && x.type === 'door' && x.enabled !== false; });
    if (!doors.length) return null;
    doors.sort(function (a, b) { return (a.wall === o.wall ? 0 : 1) - (b.wall === o.wall ? 0 : 1); });
    var d = doors[0]; return { id: d.id, code: 'the door', kind: 'door', head: n(d.frameHeightMm), width: n(d.frameWidthMm), height: n(d.frameHeightMm) };
  }
  function recalculateDesigner() { W.requestAnimationFrame(function () { var d = designer(); if (d && d.recalculate) try { d.recalculate(); } catch (e) {} }); }
  function doorHinge(o, r) { var sp = span(o.wall, r), off = n(o.offsetMm), w = n(o.frameWidthMm); return off <= sp - off - w ? 'start' : 'end'; }

  /* ---------- editor state ---------- */
  // sel: which opening on this wall is being edited ('window' or 'door').
  var S = { open: false, wall: 'back', sel: 'window', draft: null, drag: null, sizeOpen: false, msg: '', msgKind: '', undo: null, returnFocus: null };
  var ui = {};

  function flip(wall) { return wall === 'front' || wall === 'left'; }
  function committedWindow() { return windowsOnWall(S.wall)[0] || null; }
  function committedSelection() { return S.sel === 'door' ? doorOnWall(S.wall) : committedWindow(); }
  function shown() { return S.draft ? S.draft.current : committedSelection(); }
  function defaultSelection(prefer) {
    if (prefer === 'door' && doorOnWall(S.wall)) return 'door';
    if (committedWindow()) return 'window';
    if (doorOnWall(S.wall)) return 'door';
    return 'window';
  }
  function same(a, b) { return !!a && !!b && ['wall', 'offsetMm', 'sillHeightMm', 'frameWidthMm', 'frameHeightMm'].every(function (k) { return String(a[k]) === String(b[k]); }); }
  function dirty() { return !!(S.draft && (S.draft.isNew || !same(S.draft.current, S.draft.original))); }
  function say(text, kind) { S.msg = text || ''; S.msgKind = kind || ''; renderMessage(); }

  function beginDraft(o, isNew) {
    var c = clone(o); delete c.resizeMarginConflict; delete c.resizePreferredMarginsMm;
    S.draft = { id: o.id, isNew: !!isNew, original: isNew ? null : clone(o), current: c, valid: isNew ? null : true, result: null, message: '' };
    return S.draft;
  }
  function checkDraft() {
    if (!S.draft) return;
    var v = validateCandidate(S.draft.current, !S.draft.isNew);
    S.draft.valid = v.valid; S.draft.result = v.result; S.draft.message = v.message;
    if (!v.valid) say(v.message, 'error');
    else if (dirty()) say('Looks good. Tap Done to save.', 'ok');
    else say('', '');
  }
  function applyCandidate(o, label) {
    if (!o) return false;
    if (!S.draft) { var ex = committedSelection(); if (!ex) return false; beginDraft(ex, false); }
    var v = validateCandidate(o, !S.draft.isNew);
    if (!v.valid) { say(v.message, 'error'); return false; }
    S.draft.current = clone(o); S.draft.valid = true; S.draft.result = v.result; S.draft.message = '';
    say(label + '. Tap Done to save.', 'ok');
    render(); return true;
  }

  /* Save the draft. Returns true when saved (or nothing to save). */
  function commitDoor(d, c, r) {
    var o = clone(d.current);
    var ok = c.updateOpening(o.id, { wall: o.wall, offsetMm: round(o.offsetMm), frameWidthMm: round(o.frameWidthMm), frameHeightMm: round(o.frameHeightMm), hinge: doorHinge(o, r) }, r, { reason: 'windows-editor-door' });
    if (!ok) {
      var rej = c.getLastRejected && c.getLastRejected(), res = rej && rej.result;
      var msg = res ? issueMessage(authorityIssues(res, o), failure(res), o) : 'That door position was refused.';
      d.valid = false; d.message = msg; say(msg, 'error'); render(); return false;
    }
    S.draft = null; S.undo = null;
    return true;
  }
  function commitDraft() {
    var d = S.draft, c = collection(), r = room();
    if (!d) return true;
    if (!dirty()) { S.draft = null; return true; }
    if (!c || !r) { say('Changes can’t be saved yet — the designer is still loading.', 'error'); return false; }
    var o = clone(d.current);
    var v = validateCandidate(o, !d.isNew);
    if (!v.valid) { d.valid = false; d.result = v.result; d.message = v.message; say(v.message, 'error'); render(); return false; }
    if (isDoor(o)) return commitDoor(d, c, r);
    var ok, id;
    if (d.isNew) {
      id = c.addWindow({ wall: o.wall, offsetMm: o.offsetMm, sillHeightMm: o.sillHeightMm, frameWidthMm: o.frameWidthMm, frameHeightMm: o.frameHeightMm, horizontalPositionMode: o.horizontalPositionMode || 'centred', widthFixed: o.widthFixed === true }, r, { exact: true, reason: 'windows-editor-place' });
      ok = !!id;
    } else {
      ok = c.updateOpening(o.id, { wall: o.wall, offsetMm: o.offsetMm, frameWidthMm: o.frameWidthMm, frameHeightMm: o.frameHeightMm, sillHeightMm: o.sillHeightMm, horizontalPositionMode: o.horizontalPositionMode || 'centred', widthFixed: o.widthFixed === true }, r, { reason: 'windows-editor-confirm' });
      id = o.id;
    }
    if (!ok) { say('That window change was rejected.', 'error'); return false; }
    if (id && c.selectWindow) try { c.selectWindow(id, { reason: 'windows-editor-select' }); } catch (e) {}
    S.draft = null; S.undo = null;
    recalculateDesigner();
    return true;
  }
  // Before leaving the current item (other wall, other opening): save a valid change.
  function settleDraft(action) {
    if (dirty()) {
      if (S.draft.valid === false) { say('Fix this ' + noun(S.draft.current).toLowerCase() + ', or tap ✕, before ' + action + '. ' + (S.draft.message || ''), 'error'); return false; }
      if (!commitDraft()) return false;
    } else S.draft = null;
    return true;
  }

  /* ---------- actions ---------- */
  function goWall(step) {
    if (S.drag) return;
    if (!settleDraft('changing wall')) return;
    var i = WALLS.indexOf(S.wall);
    S.wall = WALLS[(i + step + WALLS.length) % WALLS.length];
    S.sel = defaultSelection();
    S.sizeOpen = false; S.undo = null; say('', '');
    render();
  }
  function selectItem(kind) {
    if (S.drag || kind === S.sel) return;
    if (!settleDraft('selecting the ' + (kind === 'door' ? 'door' : 'window'))) return;
    S.sel = kind; S.sizeOpen = false; say('', '');
    render();
  }
  function addWindow() {
    var q = snapshot(), r = room(); if (!q || !r || S.draft || committedWindow()) return;
    if (span(S.wall, r) < MIN_W) { say('This wall is too short for a window.', 'error'); return; }
    var o = { id: newId(q.openings), type: 'window', enabled: true, wall: S.wall, offsetMm: 0, frameWidthMm: NEW_W, frameHeightMm: NEW_H, sillHeightMm: NEW_SILL, horizontalPositionMode: 'centred', widthFixed: false };
    o.offsetMm = initialCentreOffset(o, q.openings, r);
    S.sel = 'window';
    beginDraft(o, true);
    checkDraft();
    if (S.draft.valid) say('New window added. Move or resize it, then tap Done.', 'ok');
    render();
  }
  // Move the door to the wall on screen. It is a draft until Done.
  function doorHere() {
    var d = getDoor(), r = room(), q = snapshot(); if (!d || !r || !q || S.drag) return;
    if (DOOR_WALLS.indexOf(S.wall) < 0) { say('The door can go on the front, left or right wall.', 'error'); return; }
    if (d.wall === S.wall) { selectItem('door'); return; }
    if (!settleDraft('moving the door')) return;
    if (span(S.wall, r) < DOOR_WIDTHS[0]) { say('This wall is too short for the door.', 'error'); return; }
    var o = clone(d); o.wall = S.wall; o.sillHeightMm = 0;
    if (o.frameWidthMm > span(S.wall, r)) o.frameWidthMm = DOOR_WIDTHS.filter(function (x) { return x <= span(S.wall, r); }).pop() || DOOR_WIDTHS[0];
    o.offsetMm = initialCentreOffset(o, q.openings.filter(function (x) { return x.id !== d.id; }), r);
    S.sel = 'door';
    beginDraft(d, false); S.draft.current = o;
    checkDraft();
    if (S.draft.valid) say('Door moved to the ' + NAMES[S.wall].toLowerCase() + '. Slide it into place, then tap Done.', 'ok');
    S.sizeOpen = false;
    render();
  }
  function alignAction(kind) {
    var o = shown(); if (!o || isDoor(o)) return null;
    var ref = alignmentReference(o); if (!ref) return null;
    if (kind === 'copy') {
      if (ref.kind !== 'window') return null;
      var centre = n(o.offsetMm) + n(o.frameWidthMm) / 2, copy = Object.assign({}, o, { frameWidthMm: round(ref.width), frameHeightMm: round(ref.height) });
      copy.offsetMm = centre - copy.frameWidthMm / 2; copy.sillHeightMm = round(ref.head - copy.frameHeightMm);
      return { ref: ref, candidate: copy, label: 'Copied the size of ' + ref.code + ' and lined up the tops' };
    }
    return { ref: ref, candidate: Object.assign({}, o, { sillHeightMm: round(ref.head - n(o.frameHeightMm)) }), label: 'Top lined up with ' + ref.code };
  }
  function doAlign(kind) {
    var a = alignAction(kind);
    if (!a) { say(kind === 'copy' ? 'There is no other window to copy.' : 'There is nothing to line up with.', 'error'); return; }
    applyCandidate(a.candidate, a.label);
  }
  function deleteWindow() {
    if (S.sel === 'door') return;
    if (S.draft && S.draft.isNew) { S.draft = null; say('New window removed.', ''); S.sel = defaultSelection(); render(); return; }
    var o = committedWindow(), c = collection(), r = room(), q = snapshot(); if (!o || !c || !r || !q) return;
    var before = clone(q.openings);
    if (!c.removeWindow(o.id, r, { reason: 'windows-editor-delete' })) { say('That window could not be deleted.', 'error'); return; }
    S.draft = null; S.sizeOpen = false; S.undo = { openings: before, room: clone(r) };
    recalculateDesigner();
    S.sel = defaultSelection();
    say('Window deleted.', 'undo'); render();
  }
  function undoDelete() {
    var u = S.undo, c = collection(), r = room(), A = solver(); if (!u || !c || !r) return;
    if (['lengthMm', 'widthMm', 'heightMm'].some(function (k) { return n(u.room[k]) !== n(r[k]); })) { S.undo = null; say('Undo isn’t available after the room size changed.', 'error'); return; }
    var p = projectFor(u.openings), v = null; try { v = A && A.evaluateProject && p ? A.evaluateProject(p) : null; } catch (e) { v = null; }
    if (!v || v.valid !== true) { S.undo = null; say('Undo isn’t possible any more.', 'error'); return; }
    if (!c.setOpenings(u.openings, r, { reason: 'windows-editor-undo' })) { say('Undo failed.', 'error'); return; }
    S.undo = null; S.sel = 'window'; recalculateDesigner(); say('Window restored.', ''); render();
  }
  function openSize() {
    var o = shown(); if (!o) return;
    S.sizeOpen = !S.sizeOpen; render();
    if (S.sizeOpen) {
      var form = isDoor(o) ? ui.doorForm : ui.sizeForm, a = form.querySelector('[name=width]');
      if (a) try { a.focus(); if (a.select) a.select(); } catch (e) {}
    }
  }
  function saveSize(e) {
    if (e) e.preventDefault();
    var o = shown(); if (!o || isDoor(o)) return;
    var width = Number(ui.sizeForm.querySelector('[name=width]').value), height = Number(ui.sizeForm.querySelector('[name=height]').value);
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < MIN_W || height < MIN_H) { say('Enter whole millimetres: width at least 400, height at least 300.', 'error'); return; }
    var head = n(o.sillHeightMm) + n(o.frameHeightMm);
    var p = Object.assign({}, o, { offsetMm: n(o.offsetMm) + (n(o.frameWidthMm) - width) / 2, frameWidthMm: width, sillHeightMm: head - height, frameHeightMm: height, widthFixed: true });
    if (applyCandidate(p, 'Size changed')) { S.sizeOpen = false; render(); }
  }
  // Door size: keep the door's centre where it is (moved inside the wall if needed).
  function saveDoorSize(e) {
    if (e) e.preventDefault();
    var o = shown(), r = room(); if (!o || !isDoor(o) || !r) return;
    var width = Number(ui.doorForm.querySelector('[name=width]').value), height = Number(ui.doorForm.querySelector('[name=height]').value);
    if (DOOR_WIDTHS.indexOf(width) < 0 || DOOR_HEIGHTS.indexOf(height) < 0) { say('Choose a width of 700, 800 or 900 mm and a height of 1900, 2000 or 2100 mm.', 'error'); return; }
    var sp = span(o.wall, r), off = round(clamp(n(o.offsetMm) + (n(o.frameWidthMm) - width) / 2, 0, Math.max(0, sp - width)));
    var p = Object.assign({}, o, { offsetMm: off, frameWidthMm: width, frameHeightMm: height, sillHeightMm: 0 });
    if (applyCandidate(p, 'Door size changed')) { S.sizeOpen = false; render(); }
  }
  function cancelAndClose() { S.draft = null; close(); }
  function doneAndClose() { if (S.drag) return; if (commitDraft()) close(); }

  /* ---------- drawing ---------- */
  function toView(wall, r, offset, width) { return flip(wall) ? span(wall, r) - n(offset) - n(width) : n(offset); }
  function pointerMm(e) {
    var svg = ui.svg, m = svg.getScreenCTM(); if (!m) return null;
    var p = svg.createSVGPoint(); p.x = e.clientX; p.y = e.clientY; p = p.matrixTransform(m.inverse());
    var r = room(); if (!r) return null;
    var H = n(r.heightMm), Wmm = span(S.wall, r);
    return { offsetMm: flip(S.wall) ? Wmm - p.x : p.x, heightMm: H - p.y };
  }
  function pxToMm(px) { var m = ui.svg && ui.svg.getScreenCTM(); return m && m.a ? px / m.a : px * 5; }

  function draw() {
    var svg = ui.svg, r = room(); if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    if (!r) { svg.setAttribute('viewBox', '0 0 100 60'); return; }
    var wall = S.wall, Wmm = span(wall, r), H = n(r.heightMm), y = function (mm) { return H - mm; };
    svg.setAttribute('viewBox', (-PAD) + ' ' + (-PAD) + ' ' + (Wmm + 2 * PAD) + ' ' + (H + 2 * PAD));
    svg.setAttribute('aria-label', NAMES[wall] + ', seen from inside');
    var g = se('g', {}, svg);
    // wall and boards
    se('rect', { x: 0, y: 0, width: Wmm, height: H, 'class': 'ta-we-wall' }, g);
    var boards = se('g', { 'class': 'ta-we-boards' }, g);
    for (var b = 95; b < Wmm; b += 95) se('line', { x1: b, y1: 0, x2: b, y2: H }, boards);
    // benches and backrests on this wall (windows can't go here; the door moves them)
    benchNoWindowZones(wall).forEach(function (z) {
      var x = toView(wall, r, z.u1, z.u2 - z.u1);
      se('rect', { x: x, y: y(z.y2), width: z.u2 - z.u1, height: z.y2 - z.y1, 'class': 'ta-we-bench' + (z.type === 'backrest' ? ' is-backrest' : '') }, g);
    });
    // the opening not being edited (tap to select it) and any other openings
    var q = snapshot(), cur = shown();
    (q && q.openings || []).forEach(function (o) {
      if (!o || o.wall !== wall || o.enabled === false) return;
      if (cur && o.id === cur.id) return;
      var x = toView(wall, r, o.offsetMm, o.frameWidthMm), sill = isDoor(o) ? 0 : n(o.sillHeightMm);
      var cls = isDoor(o) ? 'ta-we-door' : 'ta-we-other';
      var sel = isDoor(o) ? 'door' : 'window';
      var hit = se('rect', { x: x, y: y(sill + n(o.frameHeightMm)), width: n(o.frameWidthMm), height: n(o.frameHeightMm), 'class': cls + ' is-selectable', 'data-select': sel }, g);
      hit.setAttribute('aria-hidden', 'true');
      var t = se('text', { x: x + n(o.frameWidthMm) / 2, y: y(sill + n(o.frameHeightMm) / 2), 'class': 'ta-we-label' }, g);
      t.textContent = codeFor(o);
    });
    // structural end stops and head limit while editing
    var lim = S.drag && S.drag.limits || (S.draft && S.draft.result && S.draft.result.limits) || null;
    if (lim && cur) {
      if (Number.isFinite(lim.minFrameOffsetForStudsMm)) { var a = toView(wall, r, lim.minFrameOffsetForStudsMm, 0); se('line', { x1: a, y1: 0, x2: a, y2: H, 'class': 'ta-we-limit' }, g); }
      if (Number.isFinite(lim.maxFrameRightForStudsMm)) { var bb = toView(wall, r, lim.maxFrameRightForStudsMm, 0); se('line', { x1: bb, y1: 0, x2: bb, y2: H, 'class': 'ta-we-limit' }, g); }
      if (!isDoor(cur) && Number.isFinite(lim.effectiveMaxFrameHeadMm) && lim.effectiveMaxFrameHeadMm < H) se('line', { x1: 0, y1: y(lim.effectiveMaxFrameHeadMm), x2: Wmm, y2: y(lim.effectiveMaxFrameHeadMm), 'class': 'ta-we-limit' }, g);
    }
    // floor line and outline
    se('rect', { x: 0, y: 0, width: Wmm, height: H, 'class': 'ta-we-outline' }, g);
    if (!cur) {
      var hasOther = (q && q.openings || []).some(function (o) { return o && o.wall === wall && o.enabled !== false; });
      if (!hasOther) { var e = se('text', { x: Wmm / 2, y: H / 2, 'class': 'ta-we-empty' }, g); e.textContent = 'No window on this wall'; }
      return;
    }
    // the selected opening
    var door = isDoor(cur), sillCur = door ? 0 : n(cur.sillHeightMm);
    var x0 = toView(wall, r, cur.offsetMm, cur.frameWidthMm), top = y(sillCur + n(cur.frameHeightMm)), w = n(cur.frameWidthMm), h = n(cur.frameHeightMm);
    var state = !S.draft ? 'is-saved' : S.drag ? 'is-moving' : S.draft.valid === false ? 'is-invalid' : 'is-valid';
    var win = se('g', { 'class': 'ta-we-win ' + state + (door ? ' is-door' : '') }, g);
    var body = se('rect', { x: x0, y: top, width: w, height: h, 'class': 'ta-we-glass', 'data-kind': 'move' }, win);
    if (door) {
      // leaf outline and handle on the side away from the hinge
      var hingeStart = doorHinge(cur, r) === 'start', handleRight = hingeStart !== flip(wall);
      se('rect', { x: x0 + w * .08, y: top + h * .05, width: w * .84, height: h * .9, 'class': 'ta-we-leaf' }, win);
      se('line', { x1: handleRight ? x0 + w * .8 : x0 + w * .2, y1: top + h * .5, x2: handleRight ? x0 + w * .8 : x0 + w * .2, y2: top + h * .58, 'class': 'ta-we-knob' }, win);
      var dl = se('text', { x: x0 + w / 2, y: top + h * .3, 'class': 'ta-we-label' }, win); dl.textContent = 'Door';
    } else {
      se('path', { d: 'M' + x0 + ' ' + top + 'L' + (x0 + w) + ' ' + (top + h) + 'M' + (x0 + w) + ' ' + top + 'L' + x0 + ' ' + (top + h), 'class': 'ta-we-cross' }, win);
    }
    body.setAttribute('aria-hidden', 'true');
    // problem areas
    if (S.draft && S.draft.valid === false && !S.drag) {
      issuesFor(S.draft.current, S.draft.result).forEach(function (iss) {
        if (iss.u1 != null) se('rect', { x: toView(wall, r, iss.u1, iss.u2 - iss.u1), y: y(iss.y2), width: iss.u2 - iss.u1, height: iss.y2 - iss.y1, 'class': 'ta-we-issue' }, g);
        var edge = iss.edge;
        if (edge === 'u0' || edge === 'u1') {
          var physicalLeft = (edge === 'u0') !== flip(wall), ex = physicalLeft ? x0 : x0 + w;
          se('line', { x1: ex, y1: top, x2: ex, y2: top + h, 'class': 'ta-we-issue-edge' }, g);
        } else if (edge === 'y0' || edge === 'y1' || iss.kind === 'head') {
          var ey = edge === 'y0' ? top + h : top;
          se('line', { x1: x0, y1: ey, x2: x0 + w, y2: ey, 'class': 'ta-we-issue-edge' }, g);
        } else if (iss.kind === 'header-span') {
          se('line', { x1: x0, y1: top, x2: x0, y2: top + h, 'class': 'ta-we-issue-edge' }, g);
          se('line', { x1: x0 + w, y1: top, x2: x0 + w, y2: top + h, 'class': 'ta-we-issue-edge' }, g);
        }
      });
    }
    // handles (sized in screen pixels). The door has no bottom handle: it stands on the floor.
    var rad = pxToMm(12), hit2 = pxToMm(22);
    var handles = [['left', x0, top + h / 2], ['right', x0 + w, top + h / 2], ['top', x0 + w / 2, top]];
    if (!door) handles.push(['bottom', x0 + w / 2, top + h]);
    handles.forEach(function (k) {
      var hg = se('g', { 'class': 'ta-we-handle', 'data-kind': k[0] }, win);
      se('circle', { cx: k[1], cy: k[2], r: hit2, 'class': 'ta-we-hit', 'data-kind': k[0] }, hg);
      se('circle', { cx: k[1], cy: k[2], r: rad, 'data-kind': k[0] }, hg);
    });
  }

  /* ---------- pointer ---------- */
  function onPointerDown(e) {
    var t = e.target, pick = t && t.getAttribute && t.getAttribute('data-select');
    if (pick && !S.drag && !(e.button > 0)) { e.preventDefault(); selectItem(pick); return; }
    var kind = t && t.getAttribute && t.getAttribute('data-kind');
    if (!kind || S.drag || e.button > 0) return;
    var o = shown(); if (!o) return;
    if (!S.draft) beginDraft(o, false);
    var start = pointerMm(e); if (!start) return;
    var physical = kind;
    if (flip(S.wall)) { if (kind === 'left') physical = 'right'; else if (kind === 'right') physical = 'left'; }
    var live = clone(S.draft.current), check = evaluation(live, !S.draft.isNew), lim = check && check.limits || {};
    var sides = Number.isFinite(lim.minFrameOffsetForStudsMm) && Number.isFinite(lim.maxFrameRightForStudsMm) ? { min: lim.minFrameOffsetForStudsMm, max: lim.maxFrameRightForStudsMm } : null;
    var q = snapshot();
    S.drag = { kind: physical, initial: live, start: start, pointerId: e.pointerId, room: clone(room()), structuralSides: sides, limits: lim,
      benchZones: zonesFor(live), others: q ? (q.openings || []).filter(function (x) { return x.id !== live.id; }) : [], clientX: e.clientX, clientY: e.clientY, raf: 0 };
    S.sizeOpen = false;
    try { ui.svg.setPointerCapture(e.pointerId); } catch (x) {}
    e.preventDefault();
    renderControls(); draw();
  }
  function frame() {
    var d = S.drag; if (!d) return; d.raf = 0;
    var p = pointerMm({ clientX: d.clientX, clientY: d.clientY }); if (!p) return;
    var c = translateDrag(d, p);
    S.draft.current = c;
    S.draft.valid = simpleValid(c, d.others, d.room) && !overlapsBenchZones(c, d.benchZones) ? null : false;
    draw(); renderReadout();
  }
  function onPointerMove(e) { var d = S.drag; if (!d || d.pointerId !== e.pointerId) return; e.preventDefault(); d.clientX = e.clientX; d.clientY = e.clientY; if (!d.raf) d.raf = W.requestAnimationFrame(frame); }
  function onPointerUp(e) {
    var d = S.drag; if (!d || d.pointerId !== e.pointerId) return;
    e.preventDefault(); d.clientX = e.clientX; d.clientY = e.clientY;
    if (d.raf) { W.cancelAnimationFrame(d.raf); d.raf = 0; }
    frame(); S.drag = null;
    try { ui.svg.releasePointerCapture(e.pointerId); } catch (x) {}
    if (S.draft && !isDoor(S.draft.current) && d.kind === 'move' && (S.draft.current.offsetMm !== d.initial.offsetMm || S.draft.current.sillHeightMm !== d.initial.sillHeightMm)) S.draft.current.horizontalPositionMode = 'free';
    if (S.draft && !dirty() && !S.draft.isNew) { S.draft = null; say('', ''); }
    else checkDraft();
    render();
  }

  /* ---------- UI ---------- */
  var ICON = {
    back: '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 6l-6 6 6 6"></path></svg>',
    next: '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"></path></svg>',
    info: '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5M12 8h.01"></path></svg>',
    size: '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 20h4L19 9l-4-4L4 16v4z"></path></svg>',
    add: '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="4" width="16" height="16" rx="2"></rect><path d="M12 8v8M8 12h8"></path></svg>',
    align: '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 5h18"></path><rect x="5" y="5" width="5" height="9"></rect><rect x="14" y="5" width="5" height="6"></rect></svg>',
    copy: '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="8" y="8" width="12" height="12" rx="2"></rect><path d="M4 16V4h12"></path></svg>',
    del: '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"></path></svg>',
    door: '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 21V3h12v18"></path><path d="M3 21h18"></path><path d="M14 12h.01"></path></svg>',
    tick: '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12l5 5L20 7"></path></svg>'
  };
  function btn(cls, html, label) { var b = el('button', cls); b.type = 'button'; b.innerHTML = html; if (label) b.setAttribute('aria-label', label); return b; }
  function infoBtn(key, label) { var b = btn('ta-we-info', ICON.info, label); b.setAttribute('data-ta-info', key); return b; }
  function options(list) { return list.map(function (v) { return '<option value="' + v + '">' + v + '</option>'; }).join(''); }

  function build() {
    if (ui.root) return;
    var root = el('div', 'ta-we'); root.id = 'ta-windows-editor'; root.hidden = true;
    root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); root.setAttribute('aria-labelledby', 'ta-we-title');
    var inner = el('div', 'ta-we-inner'); root.appendChild(inner);

    var head = el('div', 'ta-we-head');
    var title = el('h2', 'ta-we-title', 'Windows/Door'); title.id = 'ta-we-title'; title.tabIndex = -1;
    head.appendChild(title); head.appendChild(infoBtn('windows.editor', 'How to edit windows and the door')); head.appendChild(infoBtn('windows.limits', 'Window and door limits'));
    inner.appendChild(head);

    var nav = el('div', 'ta-we-nav'); nav.setAttribute('role', 'group'); nav.setAttribute('aria-label', 'Wall');
    ui.prev = btn('ta-we-arrow', ICON.back, 'Previous wall'); ui.next = btn('ta-we-arrow', ICON.next, 'Next wall');
    ui.wallName = el('span', 'ta-we-wall-name'); ui.wallName.setAttribute('aria-live', 'polite');
    nav.appendChild(ui.prev); nav.appendChild(ui.wallName); nav.appendChild(ui.next);
    inner.appendChild(nav);

    var stage = el('div', 'ta-we-stage');
    ui.svg = se('svg', { 'class': 'ta-we-svg', role: 'img', preserveAspectRatio: 'xMidYMid meet' }, stage);
    inner.appendChild(stage);

    var read = el('div', 'ta-we-readout');
    ui.size = el('span', 'ta-we-size'); ui.meta = el('span', 'ta-we-meta');
    var rtext = el('div', 'ta-we-readtext'); rtext.appendChild(ui.size); rtext.appendChild(ui.meta);
    ui.sizeBtn = btn('ta-we-sizebtn', ICON.size + '<span>Size</span>', 'Type exact width and height');
    read.appendChild(rtext); read.appendChild(ui.sizeBtn);
    inner.appendChild(read);

    var form = el('form', 'ta-we-sizeform'); form.hidden = true; form.setAttribute('aria-label', 'Exact window size');
    form.innerHTML = '<label>Width (mm)<input name="width" type="number" inputmode="numeric" min="400" step="1"></label>' +
      '<label>Height (mm)<input name="height" type="number" inputmode="numeric" min="300" step="1"></label>' +
      '<div class="ta-we-sizeactions"><button type="button" class="ta-we-ghost" data-size-cancel>Cancel</button><button type="submit" class="ta-we-primary-sm">Apply</button></div>';
    ui.sizeForm = form; inner.appendChild(form);

    var dform = el('form', 'ta-we-sizeform ta-we-doorform'); dform.hidden = true; dform.setAttribute('aria-label', 'Door size');
    dform.innerHTML = '<label>Width (mm)<select name="width">' + options(DOOR_WIDTHS) + '</select></label>' +
      '<label>Height (mm)<select name="height">' + options(DOOR_HEIGHTS) + '</select></label>' +
      '<div class="ta-we-sizeactions"><button type="button" class="ta-we-ghost" data-size-cancel>Cancel</button><button type="submit" class="ta-we-primary-sm">Apply</button></div>';
    ui.doorForm = dform; inner.appendChild(dform);

    var tools = el('div', 'ta-we-tools'); tools.setAttribute('role', 'toolbar'); tools.setAttribute('aria-label', 'Window and door tools');
    ui.add = btn('ta-we-tool', ICON.add + '<span>Add</span>', 'Add a window'); ui.align = btn('ta-we-tool', ICON.align + '<span>Align</span>');
    ui.copy = btn('ta-we-tool', ICON.copy + '<span>Copy</span>'); ui.del = btn('ta-we-tool is-danger', ICON.del + '<span>Delete</span>', 'Delete the window');
    ui.door = btn('ta-we-tool', ICON.door + '<span>Door here</span>', 'Move the door to this wall');
    [ui.add, ui.align, ui.copy, ui.del, ui.door].forEach(function (b) { tools.appendChild(b); });
    inner.appendChild(tools);

    ui.msg = el('div', 'ta-we-msg'); ui.msg.setAttribute('role', 'status'); ui.msg.setAttribute('aria-live', 'polite');
    inner.appendChild(ui.msg);

    var foot = el('div', 'ta-we-foot');
    ui.cancel = btn('ta-we-cancel', '✕', 'Cancel changes and close');
    ui.done = btn('ta-we-done', ICON.tick + '<span>Done</span>');
    foot.appendChild(ui.cancel); foot.appendChild(ui.done);
    inner.appendChild(foot);

    D.body.appendChild(root); ui.root = root;

    ui.prev.addEventListener('click', function () { goWall(-1); });
    ui.next.addEventListener('click', function () { goWall(1); });
    ui.add.addEventListener('click', addWindow);
    ui.align.addEventListener('click', function () { doAlign('align'); });
    ui.copy.addEventListener('click', function () { doAlign('copy'); });
    ui.del.addEventListener('click', deleteWindow);
    ui.door.addEventListener('click', doorHere);
    ui.sizeBtn.addEventListener('click', openSize);
    form.addEventListener('submit', saveSize);
    dform.addEventListener('submit', saveDoorSize);
    [form, dform].forEach(function (f) { f.querySelector('[data-size-cancel]').addEventListener('click', function () { S.sizeOpen = false; render(); }); });
    ui.cancel.addEventListener('click', cancelAndClose);
    ui.done.addEventListener('click', doneAndClose);
    ui.msg.addEventListener('click', function (e) { if (e.target.closest && e.target.closest('[data-undo]')) undoDelete(); });
    ui.svg.addEventListener('pointerdown', onPointerDown);
    ui.svg.addEventListener('pointermove', onPointerMove);
    ui.svg.addEventListener('pointerup', onPointerUp);
    ui.svg.addEventListener('pointercancel', onPointerUp);
    D.addEventListener('keydown', function (e) {
      if (!S.open) return;
      if (e.key === 'Escape') {
        if (D.documentElement.classList.contains('ta-shell-sheet-open')) return; // an ⓘ sheet is open over the editor
        e.preventDefault(); if (S.sizeOpen) { S.sizeOpen = false; render(); } else cancelAndClose();
      }
    });
    W.addEventListener('resize', function () { if (S.open) draw(); });
    ['ThermoAspenOpeningCollectionUpdated', 'ThermoAspenSaunaSceneUpdated', 'ThermoAspenSaunaDesignerUpdated'].forEach(function (ev) {
      W.addEventListener(ev, function () {
        if (!S.open || S.drag || S.draft) return;
        if (!committedSelection()) S.sel = defaultSelection(S.sel);
        render();
      });
    });
  }

  function renderReadout() {
    var o = shown(), r = room();
    if (!o || !r) { ui.size.textContent = ''; ui.meta.textContent = ''; return; }
    var from = round(toView(S.wall, r, o.offsetMm, o.frameWidthMm)) + ' mm from the left';
    if (isDoor(o)) {
      ui.size.textContent = 'Door ' + round(o.frameWidthMm) + ' × ' + round(o.frameHeightMm);
      ui.meta.textContent = 'Opens outwards · ' + from;
    } else {
      ui.size.textContent = round(o.frameWidthMm) + ' × ' + round(o.frameHeightMm);
      ui.meta.textContent = 'Sill ' + round(o.sillHeightMm) + ' mm · ' + from;
    }
  }
  function renderMessage() {
    if (!ui.msg) return;
    ui.msg.className = 'ta-we-msg' + (S.msgKind ? ' is-' + S.msgKind : '');
    ui.msg.textContent = S.msg || '';
    if (S.msgKind === 'undo') { var u = el('button', 'ta-we-undo', 'Undo'); u.type = 'button'; u.setAttribute('data-undo', '1'); ui.msg.appendChild(D.createTextNode(' ')); ui.msg.appendChild(u); }
    if (!S.msg) {
      var o = shown(), other = S.sel === 'door' ? committedWindow() : doorOnWall(S.wall), text;
      if (o && isDoor(o)) text = 'Drag the door to slide it along the wall, or drag a handle to change its size.';
      else if (o) text = 'Drag the window to move it, or drag a handle to resize.';
      else text = 'Tap Add to put a window on this wall.';
      if (other) text += ' Tap the ' + (isDoor(other) ? 'door' : 'window') + ' to edit it.';
      ui.msg.textContent = text;
      ui.msg.className = 'ta-we-msg is-hint';
    }
  }
  function renderControls() {
    var o = shown(), has = !!o, door = isDoor(o), invalid = !!(S.draft && S.draft.valid === false), busy = !!S.drag;
    var winHere = !!committedWindow(), d = getDoor();
    ui.wallName.textContent = NAMES[S.wall];
    ui.prev.disabled = ui.next.disabled = busy;
    ui.add.disabled = busy || winHere || !!S.draft;
    ui.align.disabled = busy || !has || door || !alignAction('align');
    ui.copy.disabled = busy || !has || door || !alignAction('copy');
    ui.del.disabled = busy || !has || door;
    var doorAllowed = DOOR_WALLS.indexOf(S.wall) >= 0, doorShownHere = !!(d && (d.wall === S.wall || (S.draft && isDoor(S.draft.current) && S.draft.current.wall === S.wall)));
    ui.door.disabled = busy || !d || !doorAllowed || doorShownHere;
    ui.door.title = !doorAllowed ? 'The door can go on the front, left or right wall' : doorShownHere ? 'The door is on this wall' : 'Move the door to this wall';
    ui.sizeBtn.disabled = busy || !has;
    ui.sizeBtn.setAttribute('aria-expanded', S.sizeOpen ? 'true' : 'false');
    ui.sizeForm.hidden = !S.sizeOpen || !has || door;
    ui.doorForm.hidden = !S.sizeOpen || !has || !door;
    if (S.sizeOpen && o && !door) {
      var a = ui.sizeForm.querySelector('[name=width]'), b = ui.sizeForm.querySelector('[name=height]');
      if (D.activeElement !== a) a.value = round(o.frameWidthMm);
      if (D.activeElement !== b) b.value = round(o.frameHeightMm);
    }
    if (S.sizeOpen && o && door) {
      var da = ui.doorForm.querySelector('[name=width]'), dh = ui.doorForm.querySelector('[name=height]');
      var lim = S.draft && S.draft.result && S.draft.result.limits || (evaluation(o, true) || {}).limits, r0 = room();
      var okH = doorHeightsAllowed(n(r0 && r0.heightMm), lim);
      Array.prototype.forEach.call(dh.options, function (op) { var v = Number(op.value), ok = okH.indexOf(v) >= 0 || v === round(o.frameHeightMm); op.disabled = !ok; op.textContent = v + (okH.indexOf(v) >= 0 ? '' : ' (too tall for this wall)'); });
      if (D.activeElement !== da) da.value = String(nearest(DOOR_WIDTHS, round(o.frameWidthMm)));
      if (D.activeElement !== dh) dh.value = String(nearest(DOOR_HEIGHTS, round(o.frameHeightMm)));
    }
    ui.done.disabled = busy || invalid;
    ui.done.setAttribute('aria-disabled', invalid ? 'true' : 'false');
  }
  function render() { if (!ui.root) return; draw(); renderReadout(); renderControls(); renderMessage(); }

  /* ---------- open / close ---------- */
  function open(opts) {
    opts = opts || {};
    if (!collection() || !solver() || !room()) return false;
    build();
    var wall = opts.wall, door = getDoor();
    if (!wall && opts.select === 'door' && door) wall = door.wall;
    if (!wall) {
      var c = collection(), sel = c && c.getSelectedWindowId && c.getSelectedWindowId(), w = sel && getWindow(sel);
      wall = w && w.wall;
      if (!wall) { var q = snapshot(), first = q && (q.openings || []).find(function (x) { return x.type === 'window' && x.enabled !== false; }); wall = first ? first.wall : (door ? door.wall : 'back'); }
    }
    S.wall = WALLS.indexOf(wall) >= 0 ? wall : 'back';
    S.draft = null; S.drag = null; S.sizeOpen = false; S.undo = null; S.msg = ''; S.msgKind = '';
    S.sel = defaultSelection(opts.select);
    S.returnFocus = D.activeElement;
    S.open = true; ui.root.hidden = false;
    D.documentElement.classList.add('ta-we-open');
    render();
    W.requestAnimationFrame(function () { draw(); try { ui.root.querySelector('.ta-we-title').focus({ preventScroll: true }); } catch (e) {} });
    return true;
  }
  function close() {
    if (!ui.root) return;
    S.open = false; S.drag = null; S.draft = null; S.sizeOpen = false;
    ui.root.hidden = true;
    D.documentElement.classList.remove('ta-we-open');
    try { W.dispatchEvent(new CustomEvent('ThermoAspenWindowsEditorClosed', { detail: { version: VERSION } })); } catch (e) {}
    if (S.returnFocus && S.returnFocus.focus) try { S.returnFocus.focus({ preventScroll: true }); } catch (e) {}
  }

  W.ThermoAspenWindowsEditorV1 = { version: VERSION, open: open, close: close, isOpen: function () { return S.open; } };
  try { W.dispatchEvent(new CustomEvent('ThermoAspenWindowsEditorReady', { detail: { version: VERSION } })); } catch (e) {}
})(window, document);
